import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import RecordMetadata from '@/components/RecordMetadata';
import { Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { statusWorkflowService, type ServiceContext } from '@/services';

const workflowSchema = z.object({
  to_status_id: z.string().min(1, 'To Status is required'),
  transition_label: z.string().min(1, 'Transition label is required').max(100),
  seq_num: z.coerce.number().int().min(1, 'Sequence must be at least 1'),
  subtext: z.string().max(200).optional().or(z.literal('')),
  tooltip: z.string().max(500).optional().or(z.literal('')),
  confirmation_msg: z.string().max(1000).optional().or(z.literal('')),
  comments: z.enum(['no', 'optional', 'required']),
});

type WorkflowFormValues = z.infer<typeof workflowSchema>;

interface Status {
  id: string;
  name: string;
}

interface WorkflowTransition {
  id: string;
  from_status_id: string;
  to_status_id: string;
  transition_label: string;
  seq_num: number;
  subtext: string | null;
  tooltip: string | null;
  confirmation_msg: string | null;
  comments: 'no' | 'optional' | 'required';
  created: string;
  created_by: string | null;
  modified: string;
  updated_by: string | null;
  from_status?: { id: string; name: string } | null;
  to_status?: { id: string; name: string } | null;
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
}

interface WorkflowTransitionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow?: WorkflowTransition | null;
  fromStatus: Status;
  availableStatuses: Status[];
  companyId: string;
  entityType: string;
  nextSeqNum: number;
}

export default function WorkflowTransitionFormDialog({
  open,
  onOpenChange,
  workflow,
  fromStatus,
  availableStatuses,
  companyId,
  entityType,
  nextSeqNum,
}: WorkflowTransitionFormDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!workflow;

  const form = useForm<WorkflowFormValues>({
    resolver: zodResolver(workflowSchema),
    defaultValues: {
      to_status_id: '',
      transition_label: '',
      seq_num: nextSeqNum,
      subtext: '',
      tooltip: '',
      confirmation_msg: '',
      comments: 'no',
    },
  });

  useEffect(() => {
    if (workflow) {
      form.reset({
        to_status_id: workflow.to_status_id,
        transition_label: workflow.transition_label,
        seq_num: workflow.seq_num,
        subtext: workflow.subtext || '',
        tooltip: workflow.tooltip || '',
        confirmation_msg: workflow.confirmation_msg || '',
        comments: workflow.comments || 'no',
      });
    } else {
      form.reset({
        to_status_id: '',
        transition_label: '',
        seq_num: nextSeqNum,
        subtext: '',
        tooltip: '',
        confirmation_msg: '',
        comments: 'no',
      });
    }
  }, [workflow, nextSeqNum, form]);

  const getServiceContext = (): ServiceContext => ({
    companyId,
    userId: user?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (values: WorkflowFormValues) => {
      const result = await statusWorkflowService.create(
        {
          from_status_id: fromStatus.id,
          to_status_id: values.to_status_id,
          transition_label: values.transition_label,
          seq_num: values.seq_num,
          subtext: values.subtext || null,
          tooltip: values.tooltip || null,
          confirmation_msg: values.confirmation_msg || null,
          comments: values.comments,
        },
        getServiceContext()
      );
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success('Workflow transition created successfully');
      queryClient.invalidateQueries({ queryKey: ['status_workflows', companyId, entityType] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create transition: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: WorkflowFormValues) => {
      const snapshot = {
        transition_label: workflow!.transition_label,
        seq_num: workflow!.seq_num,
        subtext: workflow!.subtext,
        tooltip: workflow!.tooltip,
        confirmation_msg: workflow!.confirmation_msg,
        comments: workflow!.comments,
      };

      const result = await statusWorkflowService.update(
        workflow!.id,
        {
          transition_label: values.transition_label,
          seq_num: values.seq_num,
          subtext: values.subtext || null,
          tooltip: values.tooltip || null,
          confirmation_msg: values.confirmation_msg || null,
          comments: values.comments,
        },
        snapshot,
        getServiceContext()
      );
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success('Workflow transition updated successfully');
      queryClient.invalidateQueries({ queryKey: ['status_workflows', companyId, entityType] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update transition: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const snapshot = {
        from_status_id: workflow!.from_status_id,
        to_status_id: workflow!.to_status_id,
        transition_label: workflow!.transition_label,
        seq_num: workflow!.seq_num,
      };

      const result = await statusWorkflowService.delete(
        workflow!.id,
        snapshot,
        getServiceContext()
      );
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success('Workflow transition deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['status_workflows', companyId, entityType] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete transition: ${error.message}`);
    },
  });

  const onSubmit = (values: WorkflowFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Filter out the from status from available targets, and for edit mode, exclude existing transitions
  const selectableStatuses = availableStatuses.filter((s) => s.id !== fromStatus.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Workflow Transition' : 'Create New Transition'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the transition details below.'
              : `Create a new transition from "${fromStatus.name}" to another status.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <FormLabel>From Status</FormLabel>
              <Input value={fromStatus.name} disabled className="bg-muted" />
            </div>

            <FormField
              control={form.control}
              name="to_status_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>To Status *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger className={isEditing ? 'bg-muted' : ''}>
                        <SelectValue placeholder="Select target status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {selectableStatuses.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="transition_label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transition Label *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Submit for Review" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seq_num"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sequence *</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subtext"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subtext</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional secondary text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tooltip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tooltip</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional hover tooltip" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmation_msg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmation Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional message shown in confirmation dialog"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comments</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select comments setting" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="optional">Optional</SelectItem>
                      <SelectItem value="required">Required</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isEditing && workflow && (
              <RecordMetadata record={workflow} />
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              {isEditing && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" className="mr-auto">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Transition</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this workflow transition? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteMutation.mutate()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
